/**
 * Text Field Component
 * Wrapper for MUI TextField with react-hook-form integration
 */
import React from 'react'
import { TextField as MuiTextField } from '@mui/material'
import { Controller } from 'react-hook-form'
import { FormFieldProps } from '../types'
import { useFormContext } from '../FormContext'
import { buildValidationRules } from '../utils/validationRules'

const TextField: React.FC<FormFieldProps> = ({
  config,
  control,
  errors,
  readOnly
}) => {
  const {
    name,
    label,
    placeholder,
    required,
    disabled,
    helperText,
    fieldProps = {}
  } = config

  const {
    sx: fieldSx,
    InputProps,
    InputLabelProps,
    transformValue,
    ...otherTextFieldProps
  } = fieldProps

  // check if the field is changed
  const formContext = useFormContext()
  const formValues = formContext?.values || {}

  const isFieldChanged = () => {
    if (config.enableChangeHighlight !== true) {
      return false
    }

    if (formContext) {
      const originalValues = formContext.originalValues || {}
      const currentValues = formValues

      // compare the original value and the current value
      const originalValue = originalValues[name]
      const currentValue = currentValues[name]

      // handle the string value
      const origStrValue = originalValue?.toString() || ''
      const currStrValue = currentValue?.toString() || ''

      // only return true when the value is really different
      return origStrValue !== currStrValue
    }
    return false
  }

  // check if the field is changed
  const isChanged = isFieldChanged()

  // check if the field is read only
  const isReadOnly =
    typeof readOnly === 'boolean'
      ? readOnly
      : Array.isArray(readOnly)
        ? readOnly.includes(name)
        : false

  // 合并样式
  const mergedSx = {
    '& .MuiInputBase-root': {
      height: '36px'
    },
    ...(isChanged && {
      '& .MuiInputBase-input': {
        color: 'secondary.dark',
        fontWeight: 'bold'
      }
    }),
    ...(fieldSx || {})
  }

  // Handle blur event
  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    // Call FormBuilder's onBlur callback if provided
    if (formContext?.onBlur) {
      formContext.onBlur({ name, value: e.target.value }, formContext.values)
    }

    // Call field's onBlur if provided in fieldProps
    if (otherTextFieldProps.onBlur) {
      otherTextFieldProps.onBlur(e)
    }
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={buildValidationRules(config)}
      render={({ field }) => {
        // Ensure field.value is never undefined (always a string for text fields)
        // This ensures proper clearing when resetForm is called
        // Similar to SelectField's handling
        const fieldValue = field.value === undefined ? '' : field.value

        return (
          <MuiTextField
            {...field}
            value={fieldValue}
            label={label}
            placeholder={placeholder || otherTextFieldProps.placeholder}
            variant='outlined'
            size='small'
            fullWidth
            required={required}
            disabled={disabled || isReadOnly}
            error={!!errors[name]}
            helperText={errors[name]?.message || helperText}
            sx={mergedSx}
            InputProps={InputProps}
            InputLabelProps={InputLabelProps}
            {...otherTextFieldProps}
            onChange={e => {
              const val = transformValue
                ? transformValue(e.target.value)
                : e.target.value
              field.onChange(val)
            }}
            onBlur={e => {
              field.onBlur()
              handleBlur(e)
            }}
          />
        )
      }}
    />
  )
}

export default TextField
